/**
 * React Query query factories for CRM objects.
 */

import type { QueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';

import { logger } from '@/lib/logger';

import type { CrmObject, CrmObjectFilters } from '../../../types/crm';
import type { EntityKindRef } from '../core/CRMObjectClient';
import { createCRMObjectClient } from '../core/CRMObjectClient';
import type { EntityTypeName } from '../core/types';
import {
  getStoredObject,
  getStoreGeneration,
  resolveEntityId,
  upsertObject,
} from '../store/crmObjectStore';

export type { EntityKindRef };

/** Cache-structure discriminator: `.results` (paged) vs `.pages` (infinite). */
export type CrmObjectsListMode = 'paged' | 'infinite';

/**
 * Payload-shape discriminator for a CRM object-list query key. Historically
 * also allowed `'bodies'` (full object bodies cached in React Query); that
 * shape was retired — React Query never holds CRM object bodies, only
 * id-only sentinel entries — so this is now a single-value type by design,
 * making a body-caching list query a compile-time error.
 */
export type CrmObjectsListShape = 'ids';

/**
 * Every discriminator a CRM object-list query key needs to stay collision-free:
 * filters, page_size, and both `include_*` flags. Fold all of them into this
 * single object rather than spreading them across the key array positionally.
 */
export interface CrmObjectsListKeyParams {
  filters?: CrmObjectFilters;
  page_size?: number;
  include_associated_objects?: boolean;
  include_associations?: Record<string, { properties: string[] }> | null;
}

/**
 * Query key factory for CRM objects.
 */
export const crmQueryKeys = {
  all: (teamId: number) => ['crm', teamId] as const,
  objects: (teamId: number, entityType: EntityTypeName) =>
    [...crmQueryKeys.all(teamId), 'objects', entityType] as const,
  /** @deprecated Use `objectsListUnified` — this key omits the mode/shape/page_size discriminators. */
  objectsList: (
    teamId: number,
    entityType: EntityTypeName,
    filters?: CrmObjectFilters
  ) => [...crmQueryKeys.objects(teamId, entityType), 'list', filters] as const,
  objectDetail: (
    teamId: number,
    entityType: EntityTypeName,
    entityId: string
  ) =>
    [...crmQueryKeys.objects(teamId, entityType), 'detail', entityId] as const,
  /**
   * Canonical list-key producer. Every list query (paged or infinite) MUST
   * derive its query key from this so that distinct cache structures
   * (`.results` vs `.pages`) never collide, and so invalidators/predicates
   * can target them uniformly via `isCrmListKey`. The `shape` discriminator
   * is retained for key-position stability even though `'ids'` is now its
   * only value.
   */
  objectsListUnified: (
    teamId: number,
    entityType: EntityTypeName,
    mode: CrmObjectsListMode,
    shape: CrmObjectsListShape,
    params?: CrmObjectsListKeyParams
  ) =>
    [
      ...crmQueryKeys.objects(teamId, entityType),
      'list',
      mode,
      shape,
      params,
    ] as const,
  objectsByKind: (teamId: number, entityKind: string) =>
    [...crmQueryKeys.all(teamId), 'objects-by-kind', entityKind] as const,
  objectsListByKind: (
    teamId: number,
    entityKind: string,
    filters?: Omit<CrmObjectFilters, 'entity_type_name' | 'entity_kind'>
  ) =>
    [
      ...crmQueryKeys.objectsByKind(teamId, entityKind),
      'list',
      filters,
    ] as const,
};

/**
 * Matches any query key produced by a `crmQueryKeys` list-family producer
 * (`objectsList`, `objectsListUnified`, `objectsListByKind`). Fast-paths on
 * `key[0] !== 'crm'` — this runs inside predicates that scan the whole query
 * cache, so non-CRM keys must bail before any further inspection.
 */
export function isCrmListKey(key: readonly unknown[]): boolean {
  if (key[0] !== 'crm') {
    return false;
  }
  return key.includes('list');
}

/**
 * Options for listing CRM objects.
 */
export interface CrmObjectListOptions {
  /** Fetch associated objects in a single request (legacy, returns all associations with full metadata) */
  include_associated_objects?: boolean;
  /** Fetch specific associations with selected properties (returns raw values, not wrapped in {value: x}) */
  include_associations?: Record<string, { properties: string[] }>;
  /**
   * Whether the query should run. Defaults to true. Set to `false` to gate
   * the fetch until a required id/filter value is available (e.g. before an
   * org/team id has resolved).
   */
  enabled?: boolean;
}

/** UUID v4 pattern used to distinguish entity_id (UUID) from numeric object_id */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Query options factory for getting a single CRM object.
 *
 * Accepts either a UUID entity_id or a numeric object_id string.
 * UUIDs are routed to the `/by-entity/` endpoint; numeric IDs use
 * the generic `/crm-objects/:id/` endpoint (mirrors CrmObjectSlideout).
 *
 * Opts in to `include_associations=true` because the existing callers
 * (PipelinePage conflict-retry, useUpdateCrmProperty rollback, useFormState)
 * read `.associations` from the response — e.g. to find the current stage
 * association before applying a change. The backend defaults to opt-out
 * after the 2026-05-08 production incident where the unconditional
 * association-target permission check pinned a DB connection for many
 * seconds on high-degree entities. Lean callers that don't need
 * `.associations` should fetch via `useCrmObject` instead.
 *
 * The `queryFn` mirrors `useCrmObject` / the slideout `queryFn`: it snapshots
 * the store generation before the fetch, upserts the fetched object into
 * `crmObjectStore` when the generation is still current, and returns a
 * status-only `null` sentinel. When an org switch (or any other eviction
 * boundary) clears the store mid-fetch, the generation check skips the write
 * (logged at debug level) instead of landing stale data in the new org's
 * store. Callers that need the object body read it back from the store after
 * the fetch resolves — imperative (non-hook) call sites should use
 * `fetchCrmObjectDetail` below rather than reading `fetchQuery`'s return value.
 *
 * `staleTime: Infinity` — freshness is owned elsewhere, so this query never
 * refetches on its own; like `useCrmObject`, it's a one-shot fetch that seeds
 * the store. The store, not this cache entry, is what WebSocket events keep
 * live.
 */
export function crmObjectDetailQuery(
  teamId: number,
  entityType: EntityTypeName,
  id: string
) {
  const client = createCRMObjectClient(teamId, entityType);
  const isEntityId = UUID_RE.test(id);
  const fetchOptions = { includeAssociations: true };

  return queryOptions<null, Error, null>({
    queryKey: crmQueryKeys.objectDetail(teamId, entityType, id),
    queryFn: async (): Promise<null> => {
      // Snapshot the store generation — see the JSDoc above for the eviction-race rationale.
      const generationAtStart = getStoreGeneration();
      const data = isEntityId
        ? await client.getByEntityId(id, fetchOptions)
        : await client.get(id, fetchOptions);
      if (getStoreGeneration() !== generationAtStart) {
        logger.debug(
          '[crmObjectDetailQuery] store generation changed during fetch — discarding stale write',
          { id, entityType, teamId }
        );
        return null;
      }
      upsertObject(data);
      return null;
    },
    enabled: !!teamId && teamId > 0 && !!id,
    staleTime: Infinity,
  });
}

/**
 * Fetch a CRM object via `crmObjectDetailQuery` and read the result back out
 * of `crmObjectStore` — the queryFn upserts into the store and returns a
 * status-only `null` sentinel (see `crmObjectDetailQuery` above), so
 * imperative (non-hook) call sites that need the object body must read it
 * from the store after the fetch resolves rather than off `fetchQuery`'s
 * return value.
 *
 * Used by the mutation hooks that refetch mid-flight to resolve a 409
 * conflict or recover from a seq-ahead 400 (`useUpdateCrmProperty`,
 * `useUpdateCrmAssociation`, `useDealStageMove`, `runBackgroundSaveSubmit`).
 *
 * Throws if the store entry is missing after a successful fetch. The only
 * legitimate cause is a store-generation race (an org switch mid-fetch);
 * surfacing that as a retryable error is preferable to silently handing the
 * caller a stale snapshot to make a conflict decision against.
 */
export async function fetchCrmObjectDetail(
  queryClient: QueryClient,
  teamId: number,
  entityType: EntityTypeName,
  id: string
): Promise<CrmObject> {
  await queryClient.fetchQuery({
    ...crmObjectDetailQuery(teamId, entityType, id),
    staleTime: 0,
  });
  const fresh = getStoredObject(resolveEntityId(id))?.data;
  if (!fresh) {
    throw new Error(
      `fetchCrmObjectDetail: object ${id} not found in crmObjectStore after a successful fetch (likely a store-generation race)`
    );
  }
  return fresh;
}

// Re-export CrmObject for convenience — avoid duplicate import chains from callers.
export type { CrmObject };
